import { useState, useMemo } from 'react';
import { Card, Table, Input, Badge } from '@nexora/design-system';

// Complete CFOP table based on Brazilian fiscal classification
// Grouped by category
const CFOP_DATA = [
  // Entradas / Compras (1xxx)
  { code: '1.101', description: 'Compra para industrialização', category: 'Entradas' },
  { code: '1.102', description: 'Compra para comercialização', category: 'Entradas' },
  { code: '1.111', description: 'Compra para industrialização de mercadoria recebida anteriormente em consignação industrial', category: 'Entradas' },
  { code: '1.201', description: 'Devolução de venda de produção própria', category: 'Entradas' },
  { code: '1.202', description: 'Devolução de venda de mercadoria adquirida', category: 'Entradas' },
  // Saídas / Vendas (5xxx, 6xxx)
  { code: '5.101', description: 'Venda de produção própria', category: 'Saídas' },
  { code: '5.102', description: 'Venda de mercadoria adquirida', category: 'Saídas' },
  { code: '5.201', description: 'Devolução de compra para industrialização', category: 'Saídas' },
  { code: '5.202', description: 'Devolução de compra para comercialização', category: 'Saídas' },
  { code: '5.405', description: 'Venda de produção própria fora do estabelecimento', category: 'Saídas' },
  { code: '5.904', description: 'Remessa para venda fora do estabelecimento', category: 'Saídas' },
  // Transferências (2xxx, 3xxx)
  { code: '2.101', description: 'Transferência de produção própria', category: 'Transferências' },
  { code: '2.102', description: 'Transferência de mercadoria adquirida', category: 'Transferências' },
  { code: '3.101', description: 'Compra para industrialização interestadual', category: 'Interestaduais' },
  { code: '3.102', description: 'Compra para comercialização interestadual', category: 'Interestaduais' },
  { code: '6.101', description: 'Venda de produção própria interestadual', category: 'Interestaduais' },
  { code: '6.102', description: 'Venda de mercadoria adquirida interestadual', category: 'Interestaduais' },
  // Serviços
  { code: '7.901', description: 'Prestação de serviço de transporte', category: 'Serviços' },
  { code: '7.902', description: 'Prestação de serviço de comunicação', category: 'Serviços' },
  // Devoluções e Outras
  { code: '1.911', description: 'Entrada de mercadoria por conta de contrato de comodato', category: 'Outras' },
  { code: '5.901', description: 'Remessa para industrialização', category: 'Outras' },
  { code: '5.902', description: 'Remessa para conserto', category: 'Outras' },
  { code: '5.903', description: 'Remessa para demonstração', category: 'Outras' },
  { code: '5.906', description: 'Remessa para exposição ou feira', category: 'Outras' },
  { code: '5.907', description: 'Remessa de mercadoria para depósito fechado', category: 'Outras' },
  { code: '5.908', description: 'Remessa de bem por conta de contrato de comodato', category: 'Outras' },
];

export function CfopTablePage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  // Extract unique categories
  const categories = useMemo(() =>
    ['ALL', ...new Set(CFOP_DATA.map(c => c.category))],
    []
  );

  const filtered = useMemo(() => {
    let data = CFOP_DATA;
    if (categoryFilter !== 'ALL') {
      data = data.filter(c => c.category === categoryFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(c =>
        c.code.includes(q) || c.description.toLowerCase().includes(q)
      );
    }
    return data;
  }, [search, categoryFilter]);

  const columns = [
    { header: 'Código', accessor: 'code' as const, render: (v: string) => <code style={{ fontWeight: 600 }}>{v}</code> },
    { header: 'Descrição', accessor: 'description' as const },
    { header: 'Categoria', accessor: 'category' as const, render: (v: string) => <Badge variant="info">{v}</Badge> },
  ];

  return (
    <div className="fiscal-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Tabela CFOP</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Códigos Fiscais de Operações e Prestações.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <p style={{ color: '#43474e', fontSize: '0.875rem' }}>Código Fiscal de Operações e Prestações</p>
        </div>
      </div>
      <Card>
        <div className="filters">
          <Input placeholder="Buscar por código ou descrição..." value={search} onChange={e => setSearch(e.target.value)} />
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ padding: '0.5rem', borderRadius: 4, border: '1px solid #c4c6cf' }}>
            <option value="ALL">Todas as categorias</option>
            {categories.filter(c => c !== 'ALL').map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Table columns={columns} data={filtered} emptyMessage="Nenhum CFOP encontrado" />
      </Card>
      <style>{`
        .fiscal-page { padding: 1.5rem; }
        .filters { display: flex; gap: 1rem; margin-bottom: 1rem; }
        .filters > *:first-child { flex: 1; }
      `}</style>
    </div>
  );
}
